import {
  Box,
  Grid,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import type { SurgeonEntity, CaseTypeEntity, PreferenceCardListItemEntity } from '@peritasai/repository';
import {
  useAssetCatalog,
  useCaseTypes,
  useCreatePreferenceCard,
  useCreatePreferenceCardVersion,
  useSurgeons,
  useUntrackedAssetCatalog,
} from '@peritasai/repository';
import { AssetType, UntrackedAssetType } from '@peritasai/types';
import { SearchableDropdown, SideDrawer, Typography } from '@peritasai/ui-components';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '../../../../shared';
import { DrawerMode } from '../../utils/drawer';
import { AssetsTab } from './drawer/AssetsTab';
import { NoteDialog } from '../shared/NoteDialog';
import { SubmitButton } from '../shared/SubmitButton';
import { AssetTab } from './drawer/preference-card.types';
import type { PreferenceCardItem, TFn } from './drawer/preference-card.types';

export { DrawerMode as PreferenceCardDrawerMode };

// ─── Types ────────────────────────────────────────────────────────────────────

interface FormState {
  surgeonItem: SurgeonEntity | null;
  surgeonSearch: string;
  caseTypeItem: CaseTypeEntity | null;
  caseTypeSearch: string;
  hospitalPreferenceCardId: string;
  trackedSearch: string;
  trackedTypeFilter: AssetType | '';
  untrackedSearch: string;
  untrackedTypeFilter: UntrackedAssetType | '';
}

interface FormErrors {
  surgeon?: string;
  caseType?: string;
  hospitalPreferenceCardId?: string;
  apiError?: string;
}

interface NoteDialogState {
  open: boolean;
  itemType: AssetTab;
  itemId: string;
  initialNote: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TRACKED_TYPE_OPTIONS = [
  { value: '', label: 'All' },
  { value: AssetType.TRAY, label: 'Tray' },
  { value: AssetType.CASE_CART, label: 'Case Cart' },
  { value: AssetType.EQUIPMENT, label: 'Equipment' },
  { value: AssetType.IMPLANT, label: 'Implant' },
  { value: AssetType.STRETCHER, label: 'Stretcher' },
];

const UNTRACKED_TYPE_OPTIONS = [
  { value: '', label: 'All' },
  { value: UntrackedAssetType.SUPPLY, label: 'Supply' },
  { value: UntrackedAssetType.SINGLE_PACKED_INSTRUMENT, label: 'Single-packed' },
  { value: UntrackedAssetType.INSTRUMENT, label: 'Instrument' },
];

const getTrackedGroups = (t: TFn) => [
  { type: AssetType.TRAY, label: t('pages.entityManagement.preferenceCardDrawer.group.TRAY') },
  { type: AssetType.CASE_CART, label: t('pages.entityManagement.preferenceCardDrawer.group.CASE_CART') },
  { type: AssetType.EQUIPMENT, label: t('pages.entityManagement.preferenceCardDrawer.group.EQUIPMENT') },
  { type: AssetType.IMPLANT, label: t('pages.entityManagement.preferenceCardDrawer.group.IMPLANT') },
  { type: AssetType.STRETCHER, label: t('pages.entityManagement.preferenceCardDrawer.group.STRETCHER') },
];

const getUntrackedGroups = (t: TFn) => [
  { type: UntrackedAssetType.SUPPLY, label: t('pages.entityManagement.preferenceCardDrawer.group.SUPPLY') },
  { type: UntrackedAssetType.SINGLE_PACKED_INSTRUMENT, label: t('pages.entityManagement.preferenceCardDrawer.group.SINGLE_PACKED_INSTRUMENT') },
  { type: UntrackedAssetType.INSTRUMENT, label: t('pages.entityManagement.preferenceCardDrawer.group.INSTRUMENT') },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const emptyForm: FormState = {
  surgeonItem: null,
  surgeonSearch: '',
  caseTypeItem: null,
  caseTypeSearch: '',
  hospitalPreferenceCardId: '',
  trackedSearch: '',
  trackedTypeFilter: '',
  untrackedSearch: '',
  untrackedTypeFilter: '',
};

function validate(
  form: FormState,
  t: (key: string, options?: Record<string, string>) => string
): FormErrors {
  const errors: FormErrors = {};
  if (!form.surgeonItem) {
    errors.surgeon = t('pages.entityManagement.drawer.fieldRequired', {
      fieldName: t('pages.entityManagement.preferenceCardDrawer.surgeonLabel'),
    });
  }
  if (!form.caseTypeItem) {
    errors.caseType = t('pages.entityManagement.drawer.fieldRequired', {
      fieldName: t('pages.entityManagement.preferenceCardDrawer.caseTypeLabel'),
    });
  }
  if (!form.hospitalPreferenceCardId.trim()) {
    errors.hospitalPreferenceCardId = t('pages.entityManagement.drawer.fieldRequired', {
      fieldName: t('pages.entityManagement.preferenceCardDrawer.hospitalPreferenceCardIdLabel'),
    });
  }
  return errors;
}

function buildItemsFromTrackedAssets(selectedItem: PreferenceCardListItemEntity): PreferenceCardItem[] {
  const { caseCarts, trays, equipment, implants, stretchers } = selectedItem.trackedAssets;
  return [
    ...caseCarts.map((i) => ({ catalogItemId: i.catalogItemId, type: i.type, name: i.name, quantity: i.quantity, note: i.note ?? '' })),
    ...trays.map((i) => ({ catalogItemId: i.catalogItemId, type: i.type, name: i.name, quantity: i.quantity, note: i.note ?? '' })),
    ...equipment.map((i) => ({ catalogItemId: i.catalogItemId, type: i.type, name: i.name, quantity: i.quantity, note: i.note ?? '' })),
    ...implants.map((i) => ({ catalogItemId: i.catalogItemId, type: i.type, name: i.name, quantity: i.quantity, note: i.note ?? '' })),
    ...stretchers.map((i) => ({ catalogItemId: i.catalogItemId, type: i.type, name: i.name, quantity: i.quantity, note: i.note ?? '' })),
  ];
}

function buildItemsFromUntrackedAssets(selectedItem: PreferenceCardListItemEntity): PreferenceCardItem[] {
  const { supplies, singlePackedInstruments, instruments } = selectedItem.untrackedAssets;
  return [
    ...supplies.map((i) => ({ catalogItemId: i.untrackedAssetCatalogId, type: i.type, name: i.name, quantity: i.quantity, note: i.note ?? '' })),
    ...(singlePackedInstruments ?? []).map((i) => ({ catalogItemId: i.untrackedAssetCatalogId, type: i.type, name: i.name, quantity: i.quantity, note: i.note ?? '' })),
    ...(instruments ?? []).map((i) => ({ catalogItemId: i.untrackedAssetCatalogId, type: i.type, name: i.name, quantity: i.quantity, note: i.note ?? '' })),
  ];
}

// ─── PreferenceCardDrawer ─────────────────────────────────────────────────────

export interface PreferenceCardDrawerProps {
  open: boolean;
  mode: DrawerMode;
  selectedItem: PreferenceCardListItemEntity | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export const PreferenceCardDrawer: React.FC<PreferenceCardDrawerProps> = ({
  open, mode, selectedItem, onClose, onSuccess,
}) => {
  const { t } = useTranslation(['periverse']);

  const trackedGroups = getTrackedGroups(t);
  const untrackedGroups = getUntrackedGroups(t);

  const [form, setForm] = useState<FormState>(emptyForm);
  const [trackedItems, setTrackedItems] = useState<PreferenceCardItem[]>([]);
  const [untrackedItems, setUntrackedItems] = useState<PreferenceCardItem[]>([]);
  const [activeTab, setActiveTab] = useState<AssetTab>(AssetTab.Tracked);
  const [noteDialog, setNoteDialog] = useState<NoteDialogState>({ open: false, itemType: AssetTab.Tracked, itemId: '', initialNote: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState(false);

  const isEdit = mode === DrawerMode.Edit;
  const entityName = t('pages.entityManagement.preferenceCardDrawer.entityName');

  const debouncedSurgeonSearch = useDebounce(form.surgeonSearch);
  const debouncedCaseTypeSearch = useDebounce(form.caseTypeSearch);
  const debouncedTrackedSearch = useDebounce(form.trackedSearch);
  const debouncedUntrackedSearch = useDebounce(form.untrackedSearch);

  const { data: surgeonsData, isLoading: isSurgeonsLoading } = useSurgeons({ search: debouncedSurgeonSearch, limit: 20 });
  const surgeonOptions = surgeonsData?.surgeons ?? [];

  const { data: caseTypesData, isLoading: isCaseTypesLoading } = useCaseTypes({ search: debouncedCaseTypeSearch, limit: 20 });
  const caseTypeOptions = caseTypesData?.caseTypes ?? [];

  const { data: trackedCatalogData, isLoading: isTrackedCatalogLoading } = useAssetCatalog(
    { name: debouncedTrackedSearch, type: form.trackedTypeFilter || undefined, isActive: true, limit: 20 },
    { enabled: open && activeTab === AssetTab.Tracked }
  );
  const trackedCatalogOptions = trackedCatalogData?.assetCatalogItems ?? [];

  const { data: untrackedCatalogData, isLoading: isUntrackedCatalogLoading } = useUntrackedAssetCatalog({ search: debouncedUntrackedSearch, limit: 50 });
  const allUntrackedOptions = untrackedCatalogData?.items ?? [];
  const untrackedCatalogOptions = form.untrackedTypeFilter
    ? allUntrackedOptions.filter((i) => i.type === form.untrackedTypeFilter)
    : allUntrackedOptions;

  const createMutation = useCreatePreferenceCard();
  const versionMutation = useCreatePreferenceCardVersion();
  const isPending = createMutation.isPending || versionMutation.isPending;

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setTouched(false);
    setActiveTab(AssetTab.Tracked);

    if (mode === DrawerMode.Edit && selectedItem) {
      setTrackedItems(buildItemsFromTrackedAssets(selectedItem));
      setUntrackedItems(buildItemsFromUntrackedAssets(selectedItem));
      setForm({ ...emptyForm, surgeonSearch: selectedItem.surgeonName, caseTypeSearch: selectedItem.caseTypeDisplayName, hospitalPreferenceCardId: selectedItem.hospitalPreferenceCardId });
    } else {
      setForm(emptyForm);
      setTrackedItems([]);
      setUntrackedItems([]);
    }
  }, [open, mode, selectedItem]);

  // ─── Item handlers ───────────────────────────────────────────────────────────

  const handleAddTrackedItem = (catalogItemId: string, type: string, name: string) => {
    if (trackedItems.some((i) => i.catalogItemId === catalogItemId)) return;
    setTrackedItems((prev) => [...prev, { catalogItemId, type: type as AssetType, name, quantity: 1, note: '' }]);
  };

  const handleAddUntrackedItem = (catalogItemId: string, type: string, name: string) => {
    if (untrackedItems.some((i) => i.catalogItemId === catalogItemId)) return;
    setUntrackedItems((prev) => [...prev, { catalogItemId, type: type as UntrackedAssetType, name, quantity: 1, note: '' }]);
  };

  const handleQuantityChange = (list: AssetTab) => (id: string, delta: number) => {
    const setter = list === AssetTab.Tracked ? setTrackedItems : setUntrackedItems;
    setter((prev) => prev.map((i) => i.catalogItemId === id ? { ...i, quantity: Math.max(1, i.quantity + delta) } : i));
  };

  const handleDelete = (list: AssetTab) => (id: string) => {
    const setter = list === AssetTab.Tracked ? setTrackedItems : setUntrackedItems;
    setter((prev) => prev.filter((i) => i.catalogItemId !== id));
  };

  const handleNoteClick = (list: AssetTab) => (id: string, note: string) => {
    setNoteDialog({ open: true, itemType: list, itemId: id, initialNote: note });
  };

  const handleNoteSave = ({ note }: { note: string }) => {
    const setter = noteDialog.itemType === AssetTab.Tracked ? setTrackedItems : setUntrackedItems;
    setter((prev) => prev.map((i) => (i.catalogItemId === noteDialog.itemId ? { ...i, note } : i)));
  };

  // ─── Submit ──────────────────────────────────────────────────────────────────

  const buildItemDto = (item: PreferenceCardItem) => ({
    catalogItemId: item.catalogItemId,
    quantity: item.quantity,
    note: item.note || null,
  });

  const handleSubmit = async () => {
    if (isEdit) {
      setErrors({});
      try {
        await versionMutation.mutateAsync({
          id: selectedItem!.id,
          dto: { trackedItems: trackedItems.map(buildItemDto), untrackedItems: untrackedItems.map(buildItemDto) },
        });
        onSuccess?.();
        onClose();
      } catch (err) {
        setErrors({ apiError: err instanceof Error ? err.message : t('pages.entityManagement.drawer.errorUnexpected') });
      }
      return;
    }

    setTouched(true);
    const validationErrors = validate(form, t);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    try {
      await createMutation.mutateAsync({
        surgeonId: form.surgeonItem!.id,
        caseTypeId: form.caseTypeItem!.id,
        hospitalPreferenceCardId: form.hospitalPreferenceCardId,
        trackedItems: trackedItems.map(buildItemDto),
        untrackedItems: untrackedItems.map(buildItemDto),
      });
      setForm(emptyForm);
      setTrackedItems([]);
      setUntrackedItems([]);
      setTouched(false);
      onSuccess?.();
    } catch (err) {
      setErrors({ apiError: err instanceof Error ? err.message : t('pages.entityManagement.drawer.errorUnexpected') });
    }
  };

  const typeFilterLabel = t('pages.entityManagement.preferenceCardDrawer.typeFilterLabel');

  return (
    <>
      <SideDrawer
        open={open}
        title={isEdit ? t('pages.entityManagement.drawer.titleEdit', { entityName }) : t('pages.entityManagement.drawer.titleCreate', { entityName })}
        onClose={onClose}
        footer={
          <SubmitButton disabled={isPending} onClick={() => void handleSubmit()}>
            {isPending ? t('pages.entityManagement.drawer.submitting') : t('pages.entityManagement.drawer.submit')}
          </SubmitButton>
        }
      >
        {/* ── Header fields ─────────────────────────────────────────────── */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={6}>
            {isEdit ? (
              <TextField fullWidth size="small" disabled label={t('pages.entityManagement.preferenceCardDrawer.surgeonLabel')} value={form.surgeonSearch} />
            ) : (
              <SearchableDropdown
                options={surgeonOptions}
                getOptionLabel={(opt) => opt.fullName ?? `${opt.firstName ?? ''} ${opt.lastName ?? ''}`.trim()}
                value={form.surgeonItem}
                inputValue={form.surgeonSearch}
                loading={isSurgeonsLoading}
                label={t('pages.entityManagement.preferenceCardDrawer.surgeonLabel')}
                required
                error={touched && !!errors.surgeon}
                helperText={touched ? errors.surgeon : undefined}
                onInputChange={(value) => setForm((prev) => ({ ...prev, surgeonItem: null, surgeonSearch: value }))}
                onChange={(value) => {
                  setForm((prev) => ({ ...prev, surgeonItem: value, surgeonSearch: value?.fullName ?? `${value?.firstName ?? ''} ${value?.lastName ?? ''}`.trim() }));
                  if (touched) setErrors((prev) => ({ ...prev, surgeon: undefined }));
                }}
              />
            )}
          </Grid>
          <Grid size={6}>
            {isEdit ? (
              <TextField fullWidth size="small" disabled label={t('pages.entityManagement.preferenceCardDrawer.caseTypeLabel')} value={form.caseTypeSearch} />
            ) : (
              <SearchableDropdown
                options={caseTypeOptions}
                getOptionLabel={(opt) => opt.displayName ?? opt.caseTypeId ?? ''}
                value={form.caseTypeItem}
                inputValue={form.caseTypeSearch}
                loading={isCaseTypesLoading}
                label={t('pages.entityManagement.preferenceCardDrawer.caseTypeLabel')}
                required
                error={touched && !!errors.caseType}
                helperText={touched ? errors.caseType : undefined}
                onInputChange={(value) => setForm((prev) => ({ ...prev, caseTypeItem: null, caseTypeSearch: value }))}
                onChange={(value) => {
                  setForm((prev) => ({ ...prev, caseTypeItem: value, caseTypeSearch: value?.displayName ?? value?.caseTypeId ?? '' }));
                  if (touched) setErrors((prev) => ({ ...prev, caseType: undefined }));
                }}
              />
            )}
          </Grid>
          <Grid size={12} sx={{ mt: 1 }}>
            <TextField
              fullWidth size="small" disabled={isEdit}
              label={isEdit ? t('pages.entityManagement.preferenceCardDrawer.hospitalPreferenceCardIdLabel') : `${t('pages.entityManagement.preferenceCardDrawer.hospitalPreferenceCardIdLabel')} *`}
              value={form.hospitalPreferenceCardId}
              error={touched && !!errors.hospitalPreferenceCardId}
              helperText={touched ? errors.hospitalPreferenceCardId : undefined}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, hospitalPreferenceCardId: e.target.value }));
                if (touched) setErrors((prev) => ({ ...prev, hospitalPreferenceCardId: undefined }));
              }}
              slotProps={{ formHelperText: { sx: { ml: '5px' } } }}
            />
          </Grid>
        </Grid>

        {/* ── Asset Tabs ────────────────────────────────────────────────── */}
        <Tabs value={activeTab} onChange={(_, v: AssetTab) => setActiveTab(v)} variant="fullWidth" sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tab value={AssetTab.Tracked} label={t('pages.entityManagement.preferenceCardDrawer.trackedAssetsTab')} sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab value={AssetTab.Untracked} label={t('pages.entityManagement.preferenceCardDrawer.untrackedAssetsTab')} sx={{ textTransform: 'none', fontWeight: 600 }} />
        </Tabs>

        {activeTab === AssetTab.Tracked && (
          <AssetsTab
            searchValue={form.trackedSearch}
            typeFilter={form.trackedTypeFilter}
            catalogOptions={trackedCatalogOptions}
            isLoading={isTrackedCatalogLoading}
            items={trackedItems}
            groups={trackedGroups}
            typeFilterOptions={TRACKED_TYPE_OPTIONS}
            searchLabel={t('pages.entityManagement.preferenceCardDrawer.searchTrackedLabel')}
            typeFilterLabel={typeFilterLabel}
            onSearchChange={(v) => setForm((prev) => ({ ...prev, trackedSearch: v }))}
            onTypeFilterChange={(v) => setForm((prev) => ({ ...prev, trackedTypeFilter: v as AssetType | '' }))}
            onAddItem={handleAddTrackedItem}
            onQuantityChange={handleQuantityChange(AssetTab.Tracked)}
            onNoteClick={handleNoteClick(AssetTab.Tracked)}
            onDelete={handleDelete(AssetTab.Tracked)}
          />
        )}

        {activeTab === AssetTab.Untracked && (
          <AssetsTab
            searchValue={form.untrackedSearch}
            typeFilter={form.untrackedTypeFilter}
            catalogOptions={untrackedCatalogOptions}
            isLoading={isUntrackedCatalogLoading}
            items={untrackedItems}
            groups={untrackedGroups}
            typeFilterOptions={UNTRACKED_TYPE_OPTIONS}
            searchLabel={t('pages.entityManagement.preferenceCardDrawer.searchUntrackedLabel')}
            typeFilterLabel={typeFilterLabel}
            minWidth={130}
            onSearchChange={(v) => setForm((prev) => ({ ...prev, untrackedSearch: v }))}
            onTypeFilterChange={(v) => setForm((prev) => ({ ...prev, untrackedTypeFilter: v as UntrackedAssetType | '' }))}
            onAddItem={handleAddUntrackedItem}
            onQuantityChange={handleQuantityChange(AssetTab.Untracked)}
            onNoteClick={handleNoteClick(AssetTab.Untracked)}
            onDelete={handleDelete(AssetTab.Untracked)}
          />
        )}

        {(touched || isEdit) && errors.apiError && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: 'error.main' }}>{errors.apiError}</Typography>
          </Box>
        )}

        {touched && (errors.surgeon ?? errors.caseType) && (
          <Box sx={{ mt: 1 }}>
            {errors.surgeon && <Typography variant="body2" sx={{ color: 'error.main' }}>{errors.surgeon}</Typography>}
            {errors.caseType && <Typography variant="body2" sx={{ color: 'error.main' }}>{errors.caseType}</Typography>}
          </Box>
        )}
      </SideDrawer>

      <NoteDialog
        open={noteDialog.open}
        initialNote={noteDialog.initialNote}
        onSave={handleNoteSave}
        onClose={() => setNoteDialog((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};
